import { InfoIcon } from 'lucide-react'
import { memo, type ReactNode, useEffect, useState } from 'react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useSubscriptionState } from '@/hooks/billing/use-subscription-state.hook'
import { cn } from '@/lib/utils'

import type { BillingInterval, BillingPlan } from './billing.interface'
import { BillingIntervalToggle } from './billing-interval-toggle.atom'
import { BillingPlansSection } from './billing-plans-section.organism'

export interface BillingSubscriptionModalProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  planList: BillingPlan[]
  onSelectPlan: (plan: BillingPlan, billingInterval: BillingInterval) => void
  isLoading?: boolean
  bannerMessage?: string
  subscriptionsEnabled?: boolean
  className?: string
}

interface ModalState {
  billingInterval: BillingInterval
  setBillingInterval: (interval: BillingInterval) => void
  loadingPlanId: string | null
  setLoadingPlanId: (id: string | null) => void
}

const useModalState = (isOpen: boolean, isLoading: boolean): ModalState => {
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly')
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setBillingInterval('monthly')
      setLoadingPlanId(null)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isLoading) setLoadingPlanId(null)
  }, [isLoading])

  return { billingInterval, setBillingInterval, loadingPlanId, setLoadingPlanId }
}

const UpgradeBanner: React.FC<{ message: string }> = ({ message }) => (
  <Alert className="billing-upgrade-banner border-primary/50 bg-primary/5">
    <InfoIcon className="size-4 text-primary" />
    <AlertDescription className="text-primary">{message}</AlertDescription>
  </Alert>
)

interface PlanSelectGuards {
  isPlanCurrent: boolean
  isCancelling: boolean
  isLoading: boolean
  subscriptionsEnabled: boolean
}

const shouldSkipSelect = (guards: PlanSelectGuards): boolean => {
  const { isPlanCurrent, isCancelling, isLoading, subscriptionsEnabled } = guards
  if (!subscriptionsEnabled) return true
  if (isLoading) return true
  if (isPlanCurrent && !(isCancelling && isPlanCurrent)) return true
  return false
}

interface ModalBodyProps {
  planList: BillingPlan[]
  loadingPlanId: string | null
  billingInterval: BillingInterval
  setBillingInterval: (interval: BillingInterval) => void
  isLoading: boolean
  bannerMessage?: string
  subscriptionsEnabled: boolean
  onPlanSelect: (plan: BillingPlan) => void
}

const ModalBody: React.FC<ModalBodyProps> = ({
  planList,
  loadingPlanId,
  billingInterval,
  setBillingInterval,
  isLoading,
  bannerMessage,
  subscriptionsEnabled,
  onPlanSelect,
}) => (
  <div className="space-y-4 py-4">
    {bannerMessage && <UpgradeBanner message={bannerMessage} />}
    <div className="flex items-center justify-end">
      <BillingIntervalToggle
        billingInterval={billingInterval}
        onChange={setBillingInterval}
        isDisabled={isLoading}
        id="modal-billing-interval"
      />
    </div>
    <BillingPlansSection
      planList={planList}
      loadingPlanId={loadingPlanId}
      billingInterval={billingInterval}
      onSelectPlan={onPlanSelect}
      subscriptionsEnabled={subscriptionsEnabled}
    />
  </div>
)

const BillingSubscriptionModalBase: React.FC<BillingSubscriptionModalProps> = ({
  isOpen,
  onOpenChange,
  planList,
  onSelectPlan,
  isLoading = false,
  bannerMessage,
  subscriptionsEnabled = true,
  className,
}): ReactNode => {
  const { subscription, isCurrentPlan } = useSubscriptionState()
  const { billingInterval, setBillingInterval, loadingPlanId, setLoadingPlanId } = useModalState(
    isOpen,
    isLoading
  )

  const handlePlanSelect = (plan: BillingPlan): void => {
    const guards: PlanSelectGuards = {
      isPlanCurrent: isCurrentPlan(plan),
      isCancelling: subscription.isCancelling,
      isLoading,
      subscriptionsEnabled,
    }
    if (shouldSkipSelect(guards)) return
    setLoadingPlanId(plan.id)
    onSelectPlan(plan, billingInterval)
  }

  return (
    <Dialog open={isOpen} onOpenChange={isLoading ? undefined : onOpenChange}>
      <DialogContent className={cn('billing-subscription-modal sm:max-w-4xl', className)}>
        <DialogHeader>
          <DialogTitle>Change Plan</DialogTitle>
          <DialogDescription>Select a plan that fits your needs.</DialogDescription>
        </DialogHeader>
        <ModalBody
          planList={planList}
          loadingPlanId={loadingPlanId}
          billingInterval={billingInterval}
          setBillingInterval={setBillingInterval}
          isLoading={isLoading}
          bannerMessage={bannerMessage}
          subscriptionsEnabled={subscriptionsEnabled}
          onPlanSelect={handlePlanSelect}
        />
      </DialogContent>
    </Dialog>
  )
}

export const BillingSubscriptionModal = memo(BillingSubscriptionModalBase)

BillingSubscriptionModal.displayName = 'BillingSubscriptionModal'
